import Box from "@mui/material/Box";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import { useTranslationApp } from "../../../lib/i18next";
import {
  ADVERTISERS_AVAILABLE_PLACEHOLDER,
  BUTTON_CLOSE,
  BUTTON_CREATE,
  BUTTON_SAVE,
  EMAIL_PLACEHOLDER,
  FIELD_EMAIL_REGISTERED,
  FIELD_EMAIL_VALID,
  FIELD_PASSWORD_INSECURE,
  FIELD_REQUIRED,
  MODAL_USER_ARIA_LABEL,
  MODAL_USER_DESCRIPTION,
  MODAL_USER_DESCRIPTION_EDIT,
  MODAL_USER_TITLE,
  MODAL_USER_TITLE_EDIT,
  NAME_PLACEHOLDER,
  PASSWORD_CLARIFICATION_IN_EDIT_USER,
  PASSWORD_PLACEHOLDER,
  READY,
  ROLE_PLACEHOLDER,
  USERS_USER_CREATED,
  USERS_USER_SAVED,
} from "../../../i18n/keysTranslations";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import { useForm } from "react-hook-form";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import TextField from "../../../components/Form/TextField";
import { REGEX_EMAIL, REGEX_PASSWORD } from "../../../utils/regex";
import { useEffect, useState } from "react";
import IconButton from "../../../components/Buttons/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Select from "../../../components/Form/Select";
import RoleCapabilitiesTooltip from "../../../components/Tooltip/RoleCapabilitiesTooltip";
import { optionsAdsRoles } from "../../../utils/options";
import { auth } from "../../../lib/firebase";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import {
  startGetAdvertisersByAgency,
  startGetBusinessNameByAdvertiserIDs,
  startManageUser,
  startGetBusinessName,
} from "../../../actions/user";
import { startSendEmailUserInvitation } from "../../../actions/auth";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  ALERT_ICON_TYPE_SUCCESS,
  SUPER_ADMIN_ROLE,
} from "../../../utils/constants";
import AutocompleteField from "../../../components/Form/AutocompleteField";
import SuperAdminSelfEditModal from "./SuperAdminSelfEditModal";

const ModalUser = ({ open, onCloseModal, mode, userID }) => {
  const users = useSelectorApp((state) => state.user.users);
  const user = users?.[userID];

  if (mode === "edit" && user?.role === SUPER_ADMIN_ROLE) {
    return (
      <SuperAdminSelfEditModal
        open={open}
        onCloseModal={onCloseModal}
        userID={userID}
        user={user}
      />
    );
  }

  return (
    <ModalUserForm
      open={open}
      onCloseModal={onCloseModal}
      mode={mode}
      userID={userID}
      user={user}
    />
  );
};

const ModalUserForm = ({ open, onCloseModal, mode, userID, user }) => {
  const [isLoadingAdvertisers, setIsLoadingAdvertisers] = useState(true);
  const [advertisers, setAdvertisers] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { control, formState, handleSubmit, setValue, setError } = useForm({
    defaultValues: {
      name: "",
      email: "",
      role: "",
      password: "",
      advertisersAvailable: [],
    },
  });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const businessNameState = useSelectorApp((state) => state.user.businessName);
  const NAME_BUSINESS = businessNameState || "";

  const onChangeShowPassword = () => setShowPassword((prev) => !prev);

  const onSubmit = async (formValues) => {
    const { email } = formValues;

    setIsFetching(true);

    if (
      mode === "create" ||
      (mode === "edit" && user && user?.email !== email)
    ) {
      const result = await fetchSignInMethodsForEmail(auth, email);
      if (result.length > 0) {
        setError("email", {
          type: "manual",
          message: t(FIELD_EMAIL_REGISTERED),
        });
        setIsFetching(false);
        return;
      }
    }

    const response = await dispatch(startManageUser({ ...formValues, userID }));
    if (response) {
      if (mode === "create") {
        await dispatch(
          startSendEmailUserInvitation({
            email: formValues.email,
            name: formValues.name,
          })
        );
      }
      SimpleAlert({
        title: t(READY),
        text: t(mode === "create" ? USERS_USER_CREATED : USERS_USER_SAVED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      onCloseModal();
    }
    setIsFetching(false);
  };

  const onLoadAdvertisers = async () => {
    const responseAdvertisers = await dispatch(startGetAdvertisersByAgency());

    const advertisers = responseAdvertisers.data || [];

    const advertisersBusiness = await dispatch(
      startGetBusinessNameByAdvertiserIDs({
        advertiserIDs: advertisers,
      })
    );

    setAdvertisers(advertisersBusiness || {});
    setIsLoadingAdvertisers(false);
  };

  const onLoadUserData = () => {
    const { name, email, role, advertisersAvailable = [] } = user;
    setValue("name", name);
    setValue("email", email);
    setValue("role", role);

    if (advertisersAvailable.length > 0) {
      setValue("advertisersAvailable", advertisersAvailable);
    } else {
      setValue("advertisersAvailable", Object.keys(advertisers || {}) || []);
    }
  };

  useEffect(() => {
    if (mode !== "edit") return;
    if (isLoadingAdvertisers) return;
    if (!userID) return;

    if (!user) {
      onCloseModal();
      return;
    }

    onLoadUserData();
    // eslint-disable-next-line
  }, [isLoadingAdvertisers, mode, userID, user]);

  useEffect(() => {
    dispatch(startGetBusinessName());
    onLoadAdvertisers();
    // eslint-disable-next-line
  }, []);

  const optionsAdvertisers = Object.keys(advertisers).map((advertiserID) => ({
    label: advertisers[advertiserID],
    value: advertiserID,
  }));

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onCloseModal}
      fullWidth={true}
      maxWidth="xs"
      scroll="paper"
      ariaLabel={t(MODAL_USER_ARIA_LABEL)}
      title={t(mode === "create" ? MODAL_USER_TITLE : MODAL_USER_TITLE_EDIT)}
      description={t(
        mode === "create"
          ? MODAL_USER_DESCRIPTION
          : MODAL_USER_DESCRIPTION_EDIT,
        { NAME_BUSINESS }
      )}
      sxTitle={{ color: "background.paperContrastText" }}
      sxDescription={{ color: "background.paperContrastText" }}
    >
      <Box
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <TextField
          fullWidth
          label={t(NAME_PLACEHOLDER)}
          name="name"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          id="email"
          label={t(EMAIL_PLACEHOLDER)}
          name="email"
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: {
              emailValid: (value) => {
                if (!REGEX_EMAIL.test(value)) {
                  return t(FIELD_EMAIL_VALID);
                }
              },
            },
          }}
          errors={errors}
        />
        <Select
          fullWidth
          id="role"
          label={t(ROLE_PLACEHOLDER)}
          name="role"
          variant="filled"
          color="primary"
          doTranslate={true}
          options={optionsAdsRoles}
          renderTrailing={({ option }) => (
            <RoleCapabilitiesTooltip role={option.value} stopPropagation />
          )}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <AutocompleteField
          fullWidth
          label={t(ADVERTISERS_AVAILABLE_PLACEHOLDER)}
          name="advertisersAvailable"
          control={control}
          multiple={true}
          options={optionsAdvertisers}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          id="password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onChangeShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          helperText={mode === "edit" && t(PASSWORD_CLARIFICATION_IN_EDIT_USER)}
          control={control}
          rules={{
            required: {
              value: false,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (mode === "create") {
                if (!value) {
                  return t(FIELD_REQUIRED);
                }
                if (!REGEX_PASSWORD.test(value)) {
                  return t(FIELD_PASSWORD_INSECURE);
                }
              }
              if (mode === "edit" && value && !REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
            },
          }}
          errors={errors}
        />
        <ContainerModalActions
          hasPadding={false}
          propsPrimaryButton={{
            showButton: true,
            text: mode === "create" ? BUTTON_CREATE : BUTTON_SAVE,
            loading: isFetching,
            onClick: () => {},
          }}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CLOSE,
            loading: isFetching,
            onClick: onCloseModal,
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalUser;
